import {
  Home,
  Calendar,
  MapPin,
  Users,
  Shield,
  Ticket,
  BarChart3,
  ClipboardList,
  Mail,
  ScrollText,
  Settings,
  Building2,
  ChartColumn,
  CreditCard,
  type LucideIcon,
} from "lucide-react";
import { Permission } from "@/types/roles";

export type ZoneKind = "flat" | "event" | "ekip" | "admin";

/** Mirrors the backend RolesGuard hierarchy — @Roles() compares by level. */
export const ROLE_LEVEL: Record<string, number> = {
  SYSTEM_ADMIN: 5,
  COMPANY_OWNER: 4,
  MANAGER: 3,
  EMPLOYEE: 2,
  GATE_KEEPER: 1,
};

export interface NavEntry {
  key: string;
  href: string; // un-localized, leading "/"
  icon: LucideIcon;
  fallback: string; // Turkish fallback label
  permission?: Permission; // omit = always
  minRole?: string; // UserRole floor, for endpoints guarded by @Roles()
  sysadmin?: boolean; // true = system-admin only
  dictKey?: string; // key under dict.layout.sidebar (if existing)
}

export const GLOBAL_ZONES: NavEntry[] = [
  {
    // /dashboard reads /activity-logs, which the backend guards with
    // @Roles(COMPANY_OWNER, MANAGER, SYSTEM_ADMIN) — a UserRole floor, not a
    // permission. A custom role holding VIEW_STATISTICS still gets a 403, so
    // gating this on the permission would keep landing EMPLOYEE and
    // GATE_KEEPER accounts on an error screen.
    key: "home",
    href: "/dashboard",
    icon: Home,
    fallback: "Anasayfa",
    minRole: "MANAGER",
    dictKey: "home",
  },
  {
    key: "events",
    href: "/events",
    icon: Calendar,
    fallback: "Etkinlikler",
    permission: Permission.VIEW_EVENTS,
    dictKey: "events",
  },
  {
    key: "venues",
    href: "/venues",
    icon: MapPin,
    fallback: "Mekanlar",
    permission: Permission.VIEW_VENUES,
    dictKey: "venues",
  },
  {
    key: "earnings",
    href: "/earnings",
    // Sayfa artik "Raporlar"; cuzdan ikonu yalnizca odemeyi anlatiyordu.
    icon: ChartColumn,
    fallback: "Raporlar",
    // VIEW_OWN_EARNINGS sahip, mudur ve PR'da var; kapida yok. Sayfanin
    // kendisi PR'a yalnizca kendi satirini gosteriyor.
    permission: Permission.VIEW_OWN_EARNINGS,
    dictKey: "earnings",
  },
  {
    key: "ekip",
    href: "/users",
    icon: Users,
    fallback: "Kullanıcılar",
    permission: Permission.VIEW_USERS,
    dictKey: "users",
  },
  {
    key: "admin",
    href: "/admin",
    icon: Shield,
    fallback: "Admin",
    sysadmin: true,
    dictKey: "adminDashboard",
  },
];

export const EVENT_PANEL_ITEMS: NavEntry[] = [
  {
    key: "guests",
    href: "/guests",
    icon: Ticket,
    fallback: "Davetliler",
    permission: Permission.VIEW_GUESTS,
    dictKey: "guests",
  },
  {
    key: "statistics",
    href: "/statistics",
    icon: BarChart3,
    fallback: "Gece Raporu",
    permission: Permission.VIEW_STATISTICS,
    dictKey: "statistics",
  },
  {
    key: "guestLists",
    href: "/guest-lists",
    icon: ClipboardList,
    fallback: "Davetli Listeleri",
    permission: Permission.VIEW_GUEST_LISTS,
    dictKey: "guestLists",
  },
  {
    key: "invitations",
    href: "/invitations",
    icon: Mail,
    fallback: "Davetiyeler",
    permission: Permission.VIEW_INVITATIONS,
    dictKey: "invitations",
  },
  {
    key: "log",
    href: "/log",
    icon: ScrollText,
    fallback: "Log",
    permission: Permission.VIEW_STATISTICS,
    dictKey: "log",
  },
  {
    key: "settings",
    href: "/settings",
    icon: Settings,
    fallback: "Ayarlar",
    permission: Permission.UPDATE_EVENT,
    dictKey: "settings",
  },
];

export const EKIP_PANEL_ITEMS: NavEntry[] = [
  {
    key: "users",
    href: "/users",
    icon: Users,
    fallback: "Kullanıcılar",
    permission: Permission.VIEW_USERS,
    dictKey: "users",
  },
  {
    key: "roles",
    href: "/roles",
    icon: Shield,
    fallback: "Roller",
    permission: Permission.VIEW_USERS,
    dictKey: "roles",
  },
];

export const ADMIN_PANEL_ITEMS: NavEntry[] = [
  {
    key: "adminOverview",
    href: "/admin",
    icon: BarChart3,
    fallback: "Genel Bakış",
  },
  {
    key: "companies",
    href: "/admin/companies",
    icon: Building2,
    fallback: "Şirketler",
  },
  {
    key: "companyManagement",
    href: "/admin/companies-management",
    icon: Building2,
    fallback: "Şirket Yönetimi",
    dictKey: "companyManagement",
  },
  {
    key: "subscriptionPlans",
    href: "/admin/subscription-plans",
    icon: CreditCard,
    fallback: "Abonelik Planları",
    dictKey: "subscriptionPlans",
  },
  {
    key: "adminUsers",
    href: "/admin/users",
    icon: Users,
    fallback: "Sistem Kullanıcıları",
    dictKey: "adminUsers",
  },
];

export function detectEventId(
  pathname: string,
  lang: string,
): string | undefined {
  const p = pathname.replace(new RegExp(`^/${lang}`), "");
  const m = p.match(/^\/events\/([^/]+)(?:\/|$)/);
  if (!m) return undefined; // "/events" (list) → no capture
  if (m[1] === "new") return undefined; // create route, not an event
  return m[1]; // "/events/{id}" overview AND its sub-routes = event-context
}

export function detectZone(pathname: string, lang: string): ZoneKind {
  const p = pathname.replace(new RegExp(`^/${lang}`), "");
  if (detectEventId(pathname, lang)) return "event";
  if (p.startsWith("/admin")) return "admin";
  if (p.startsWith("/users") || p.startsWith("/roles")) return "ekip";
  return "flat";
}

export interface PermCtx {
  hasPermission: (p: Permission) => boolean;
  isSystemAdmin: boolean;
  role?: string | null;
}

export function filterByPermission(
  items: NavEntry[],
  ctx: PermCtx,
): NavEntry[] {
  const level = ctx.isSystemAdmin
    ? ROLE_LEVEL.SYSTEM_ADMIN
    : (ROLE_LEVEL[ctx.role ?? ""] ?? 0);
  return items.filter((i) => {
    if (i.sysadmin && !ctx.isSystemAdmin) return false;
    if (i.minRole && level < (ROLE_LEVEL[i.minRole] ?? 0)) return false;
    if (i.permission && !ctx.hasPermission(i.permission)) return false;
    return true;
  });
}

export function isActive(pathname: string, localizedHref: string): boolean {
  return pathname === localizedHref || pathname.startsWith(localizedHref + "/");
}

/** First global zone the role can actually open — where a login should land. */
export function landingHref(ctx: PermCtx): string {
  return filterByPermission(GLOBAL_ZONES, ctx)[0]?.href ?? "/profile";
}

/** Whether a global zone is reachable for this role — same rule as the menu. */
export function canOpenZone(key: string, ctx: PermCtx): boolean {
  const entry = GLOBAL_ZONES.find((z) => z.key === key);
  return entry ? filterByPermission([entry], ctx).length === 1 : false;
}
